<template>
  <div class="item-package-form">
    <fieldset :disabled="isReadOnly" class="item-package-fieldset">
      <b-row>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('basicData') }}</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('name')"
            label-for="name"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input v-model="localItemPackage.name" type="text" :placeholder="$t('name')" />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('activeState')"
            label-for="active"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :placeholder="$t('activeState') + ' ' + $t('select')"
              v-model="active"
              :options="activeOptions"
              :show-labels="false"
              :allow-empty="false"
              :disabled="isReadOnly"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <b-badge :variant="getActiveBadgeColor(option)">{{ $t(option) }}</b-badge>
              </template>
              <template slot="option" slot-scope="{ option }">
                <b-badge :variant="getActiveBadgeColor(option)">
                  {{ $t(option) }}
                </b-badge>
              </template>
            </multiselect>
          </b-form-group>

          <b-form-group
            v-if="localItemPackage.number && localItemPackage.number !== '_new'"
            horizontal
            :label="$t('item_package_number')"
            label-for="itemPackageNumber"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ localItemPackage.number.toString().padStart(4, '0') }}
            </div>
          </b-form-group>

          <b-form-group
            v-if="localItemPackage.createdBy"
            horizontal
            :label="$t('createdBy')"
            label-for="createdBy"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ localItemPackage.createdBy.firstname }} {{ localItemPackage.createdBy.lastname
              }}<span v-if="localItemPackage.createdAt"
                >, {{ $dayjs(localItemPackage.createdAt).format('DD.MM.YYYY HH:mm') }}</span
              >
            </div>
          </b-form-group>

          <b-form-group
            v-if="localItemPackage.updatedBy"
            horizontal
            :label="$t('updatedBy')"
            label-for="updatedBy"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ localItemPackage.updatedBy.firstname }} {{ localItemPackage.updatedBy.lastname
              }}<span v-if="localItemPackage.updatedAt"
                >, {{ $dayjs(localItemPackage.updatedAt).format('DD.MM.YYYY HH:mm') }}</span
              >
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Package Items Section -->
      <b-row>
        <b-col lg="12" md="12" sm="12">
          <div class="package-items-section">
            <div class="section-header mb-2 mt-4" style="border-bottom: 1px solid #dee2e6">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <legend class="mb-0">
                  <strong>{{ $t('item_inventory') }}</strong>
                </legend>
                <div class="d-flex gap-2">
                  <button
                    v-if="!isReadOnly"
                    class="btn btn-success ml-2"
                    @click="openItemBrowser"
                    type="button"
                    style="white-space: nowrap; font-size: 0.875rem;"
                  >
                    <i class="fa fa-plus mr-1"></i>
                    {{ $t('add_items') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Current Package Items -->
            <div class="current-items mb-3">
              <PackageItems
                :items="localItemPackage.items || []"
                @update-items="updatePackageItems"
              />
            </div>
          </div>
        </b-col>
      </b-row>
    </fieldset>

    <!-- Item Browser Modal -->
    <Dialog
      :header="$t('add_items')"
      :visible.sync="showItemBrowserModal"
      :modal="true"
      :style="{ width: '90vw', maxWidth: '1600px' }"
      :contentStyle="{ height: '70vh', overflow: 'visible' }"
      :closable="true"
    >
      <ItemBrowser
        context="package"
        :show-pricing="false"
        :show-supplier-filter="false"
        default-view-mode="cards"
        @add="addItemToPackage"
      />

      <template #footer>
        <button class="btn btn-default" @click="closeItemBrowser">
          {{ $t('close') }}
        </button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import ItemBrowser from '@/components/ItemBrowser.vue';
import PackageItems from '@/components/PackageItems.vue';
import Dialog from 'primevue/dialog';
import Multiselect from 'vue-multiselect';
import { mapGetters } from 'vuex';

export default {
  name: 'ItemPackageForm',
  components: {
    ItemBrowser,
    PackageItems,
    Dialog,
    Multiselect,
  },
  props: {
    itemPackage: {
      type: Object,
      required: true,
    },
    /** Viewer on the view item-packages: shows the package, opens no item browser. */
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localItemPackage: { ...this.itemPackage },
      showItemBrowserModal: false,
    };
  },
  computed: {
    ...mapGetters(['getEnumValues']),
    active: {
      get() {
        const activeValue =
          this.localItemPackage.active !== undefined ? this.localItemPackage.active : true;
        return activeValue ? 'YES' : 'NO';
      },
      set(value) {
        this.localItemPackage.active = value === 'YES';
        this.emitUpdate();
      },
    },
    activeOptions() {
      return ['YES', 'NO'];
    },
  },
  watch: {
    itemPackage: {
      handler(newItemPackage) {
        console.log('ItemPackageForm: itemPackage changed', newItemPackage);
        this.localItemPackage = { ...newItemPackage };
      },
      deep: true,
      immediate: true,
    },
    localItemPackage: {
      handler() {
        this.emitUpdate();
      },
      deep: true,
    },
  },
  methods: {
    getActiveBadgeColor(activeValue) {
      return activeValue === 'YES' ? 'success' : 'danger';
    },
    emitUpdate() {
      this.$emit('update:itemPackage', this.localItemPackage);
    },

    openItemBrowser() {
      this.showItemBrowserModal = true;
    },

    closeItemBrowser() {
      this.showItemBrowserModal = false;
    },

    addItemToPackage(item) {
      console.log('Adding item to package:', item);

      // Ensure items array exists
      if (!this.localItemPackage.items) {
        this.localItemPackage.items = [];
      }

      // Check if item already exists in package
      const existingItemIndex = this.localItemPackage.items.findIndex(
        (packageItem) => packageItem.item._id === item._id || packageItem.item === item._id
      );

      let updatedItems;
      const quantityToAdd = parseInt(item.addRemoveCount || item.quantity || 1, 10);

      if (existingItemIndex >= 0) {
        // Update quantity of existing item
        updatedItems = [...this.localItemPackage.items];
        const currentQuantity = parseInt(updatedItems[existingItemIndex].quantity || 0, 10);
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: currentQuantity + quantityToAdd,
        };
      } else {
        // Add new item to package
        const packageItem = {
          item: item, // Full item object (will be transformed to _id in createPayload)
          quantity: quantityToAdd,
        };

        updatedItems = [...this.localItemPackage.items, packageItem];
      }

      // Update local package
      this.localItemPackage.items = updatedItems;
      this.emitUpdate();
    },

    updatePackageItems(updatedItems) {
      this.localItemPackage.items = updatedItems;
      this.emitUpdate();
    },
  },
};
</script>

<style scoped lang="scss">
.p-calendar {
  width: 100%;
}

.form-actions {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
  margin-top: 1rem;
}

.section-header legend {
  border-bottom: none;
}

.item-package-fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
</style>
